import * as Sentry from "@sentry/react";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useStore } from "zustand";
import { FeedbackButton } from "@/components/app/feedback-button";
import { ScreenShareIndicator } from "@/components/app/screen-share-indicator";
import { WorkspaceRevoked } from "@/components/app/workspace-revoked";
import { useWsConnected } from "@/hooks/use-ws-connected";
import { initActivityTracker } from "@/lib/activity-tracker";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { claimSocket } from "@/lib/socket-owner";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";

export const Route = createFileRoute("/app")({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate();
	const user = useStore(authStore, (state) => state.user);
	const initialized = useStore(authStore, (state) => state.initialized);
	const { isConnected, reconnectCountdown, connectionEpoch } = useWsConnected();

	const logout = useStore(authStore, (state) => state.logout);
	const loadOnConnect = useStore(coreStore, (state) => state.loadOnConnect);
	// D2: hydrated cached channels let us paint /app before the WS connects. Only
	// a cold cache miss falls back to the full-screen spinner.
	const hasShell = useStore(coreStore, (state) => state.channels.size > 0);
	// TECH-2958: terminal full-screen states for cold (uncached) loads.
	const connectStatus = useStore(coreStore, (state) => state.connectStatus);
	// Which way access ended, so the terminal screen below says the true thing
	// (TECH-3253).
	const revokedReason = useStore(coreStore, (state) => state.revokedReason);
	// Decides the revoked screen's action (TECH-3259). `revokeWorkspaceAccess`
	// has already removed the revoked workspace from this map, so anything left
	// is somewhere the student can actually go; with nothing left, offering the
	// picker would just reload into the `no-workspaces` screen below.
	const hasOtherWorkspaces = useStore(
		coreStore,
		(state) => state.workspaces.size > 0
	);

	// Take the tab's single socket for the `/app` session. A no-op unless we are
	// arriving from /operations, which holds its own session and may have it.
	useEffect(() => {
		claimSocket("app").catch((error) => {
			console.error("Failed to claim the socket for /app:", error);
		});
	}, []);

	useEffect(() => {
		if (initialized && !user) {
			navigate({ to: "/" });
			if (SENTRY_ENABLED) {
				Sentry.setUser(null);
			}
		} else if (user && SENTRY_ENABLED) {
			Sentry.setUser({
				id: user.uid,
				email: user.email || undefined,
			});
		}
	}, [initialized, user, navigate]);

	useEffect(() => {
		if (!isConnected) return;

		// Sequential one-in-flight retrieval chain (workspace.setCurrent →
		// channel.list → message.list → users.list → roles.list). The store's
		// generation token abandons any chain superseded by a newer run, so rapid
		// reconnects collapse to just the latest chain. TECH-3051: `connectionEpoch`
		// re-fires this on EVERY reconnect. The grace period keeps `isConnected`
		// true across a fast close→open, so without the epoch the reconnect would
		// skip the reload and leave the server connection workspace-less
		// (stale users → author names render as raw Firebase UIDs).
		loadOnConnect();
	}, [isConnected, connectionEpoch, loadOnConnect]);

	// Activity tracker is mount-scoped, so initialise once, not on every reconnect.
	useEffect(() => initActivityTracker(), []);

	if (!(initialized && user)) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-muted-foreground">Loading...</p>
			</div>
		);
	}

	// Terminal: access to the workspace on screen was revoked mid-session
	// (TECH-3259). Checked ahead of the remaining states, including the
	// `hasShell` fallback, which would otherwise keep rendering the cached
	// workspace we have just been removed from. Where the student goes next
	// depends on what they have left. A reload lands them in a remaining
	// workspace (the revoked one is gone from the server's list and its cached
	// channels have been dropped), and with none left the only honest action is
	// to sign out rather than reload into the `no-workspaces` screen below.
	if (connectStatus === "revoked") {
		return (
			<WorkspaceRevoked
				hasOtherWorkspaces={hasOtherWorkspaces}
				onContinue={() => window.location.reload()}
				onLogout={logout}
				reason={revokedReason ?? "removed"}
			/>
		);
	}

	// Terminal: the user has no workspaces (list loaded, empty). Persistent state,
	// not a transient load, so show a clear message rather than a blank page.
	if (connectStatus === "no-workspaces") {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
				<p className="font-medium">No workspaces available</p>
				<p className="text-muted-foreground text-sm">
					You don't have access to any workspaces yet. Contact your program
					admin.
				</p>
				<button
					className="rounded-md bg-primary px-4 py-2 text-primary-foreground text-sm"
					onClick={logout}
					type="button"
				>
					Log out
				</button>
			</div>
		);
	}

	// Terminal: critical data failed all retries AND there is nothing cached to
	// fall back to. (With a cache, we keep rendering it read-only plus a toast.)
	if (connectStatus === "error" && !hasShell) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
				<p className="font-medium">Couldn't load your workspace</p>
				<p className="text-muted-foreground text-sm">
					Something went wrong loading your channels. Please refresh to try
					again.
				</p>
				<button
					className="rounded-md bg-primary px-4 py-2 text-primary-foreground text-sm"
					onClick={() => window.location.reload()}
					type="button"
				>
					Refresh
				</button>
			</div>
		);
	}

	// Cold cache miss (first-ever load, or a workspace with nothing cached), so
	// keep the full-screen spinner until the chain populates channels.
	if (!(isConnected || hasShell)) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-3">
				<Loader2 className="size-6 animate-spin text-muted-foreground" />
				<p className="text-muted-foreground">Loading learning environment...</p>
			</div>
		);
	}

	return (
		<>
			{!isConnected && (
				// Read-only channels painted from cache while the live connection comes
				// up. Single source of connection-status UI (the grid no longer renders
				// its own overlay), and it carries the reconnect countdown for long
				// backoffs.
				<div className="-translate-x-1/2 fixed top-2 left-1/2 z-50 flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-muted-foreground text-xs shadow-sm">
					<Loader2 className="size-3 animate-spin" />
					{reconnectCountdown === null
						? "Connecting…"
						: `Reconnecting in ${
								reconnectCountdown > 60
									? `${Math.ceil(reconnectCountdown / 60)} min`
									: `${reconnectCountdown}s`
							}…`}
				</div>
			)}
			<Outlet />
			<FeedbackButton />
			<ScreenShareIndicator />
		</>
	);
}
